import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import RecordVoiceOverRoundedIcon from "@mui/icons-material/RecordVoiceOverRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import HearingRoundedIcon from "@mui/icons-material/HearingRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";

export default function DeEsserLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const pageUrl = "https://parishaudio.com/learning/de-esser";
  const pageTitle = "De-Esser in Music Production: What It Is and How It Works";
  const pageDescription =
    "Learn what a de-esser does, how it reduces harsh sibilance, where it is used on vocals and dialogue, and how it differs from EQ and compression.";

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        headline: pageTitle,
        description: pageDescription,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
        },
        url: pageUrl,
        inLanguage: "en-US",
        articleSection: "Audio Education",
        keywords: [
          "de esser",
          "de-esser",
          "what does a de esser do",
          "vocal sibilance",
          "reduce harsh s sounds",
          "de esser vs eq",
          "de esser vs compressor",
        ],
        author: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
        },
        publisher: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
          logo: {
            "@type": "ImageObject",
            url: "https://parishaudio.com/assets/logo.png",
          },
        },
        image: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/RecordingStudioStockImage1.jpg",
        },
        about: [
          { "@type": "Thing", name: "De-esser" },
          { "@type": "Thing", name: "Vocal sibilance" },
          { "@type": "Thing", name: "Dialogue processing" },
          { "@type": "Thing", name: "Equalization" },
          { "@type": "Thing", name: "Compression" },
          { "@type": "Thing", name: "Music production" },
        ],
        isPartOf: {
          "@type": "CollectionPage",
          name: "Parish Audio Learning Center",
          url: "https://parishaudio.com/learning",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://parishaudio.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Learning Center",
            item: "https://parishaudio.com/learning",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "De-Esser",
            item: pageUrl,
          },
        ],
      },
    ],
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "A de-esser reduces harsh sibilant sounds such as S, SH, and CH, usually by turning down a targeted high-frequency range only when it becomes too strong.",
      icon: <HearingRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "Too much sibilance can make a vocal or spoken voice sound sharp, fatiguing, and difficult to listen to, especially in bright modern mixes.",
      icon: <RecordVoiceOverRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Lead vocals, backing vocals, dialogue, podcasts, voiceovers, and sometimes bright instruments that need occasional high-frequency control.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const deEsserTypes = [
    {
      name: "Broadband De-Esser",
      description:
        "Turns down the full signal when sibilance is detected, which can sound smooth but may be more noticeable if overused.",
    },
    {
      name: "Split-Band De-Esser",
      description:
        "Only reduces the offending high-frequency range, often preserving the rest of the signal more transparently.",
    },
    {
      name: "Frequency-Selective De-Esser",
      description:
        "Targets a very specific band where the harshness lives, which can be highly effective when tuned correctly.",
    },
    {
      name: "Dynamic EQ Approach",
      description:
        "Acts similarly to a de-esser by reducing a chosen band only when it becomes excessive, often with more precise control.",
    },
    {
      name: "Multiband De-Esser",
      description:
        "Lets you control several regions independently, which can be useful when harshness exists in more than one area.",
    },
    {
      name: "Manual / Clip-Gain De-Essing",
      description:
        "Instead of using a plugin, the engineer manually turns down specific harsh syllables for maximum precision.",
    },
  ];

  const deEsserConcepts = [
    {
      name: "Sibilance",
      description:
        "Sibilance is the bright hiss-like energy in consonants such as S, SH, and CH. Too much of it can sound piercing and distracting.",
    },
    {
      name: "Detection Range",
      description:
        "A de-esser listens to a targeted part of the frequency spectrum and reacts when that area becomes too loud.",
    },
    {
      name: "Dynamic Reduction",
      description:
        "Unlike static EQ, a de-esser only reduces the problem when it appears, leaving the sound mostly untouched the rest of the time.",
    },
    {
      name: "Control Without Dulling",
      description:
        "The goal is not to remove brightness completely. The goal is to reduce harshness while keeping clarity and intelligibility.",
    },
  ];

  const controls = [
    {
      name: "Frequency",
      description:
        "Sets the area of the spectrum where the de-esser focuses. This is where the harsh consonants are being detected and reduced.",
    },
    {
      name: "Threshold",
      description:
        "Determines how strong the sibilance must be before the de-esser starts working.",
    },
    {
      name: "Amount / Range",
      description:
        "Controls how much reduction the de-esser applies when it engages.",
    },
    {
      name: "Bandwidth / Q",
      description:
        "Shapes how narrow or wide the targeted frequency region is, which affects how focused or broad the processing feels.",
    },
    {
      name: "Monitor / Listen",
      description:
        "Lets you hear what the detector is focusing on, which makes it much easier to find the harsh region accurately.",
    },
    {
      name: "Mode",
      description:
        "Some de-essers let you switch between broadband and split-band behavior depending on the type of result you want.",
    },
  ];

  const commonUses = [
    "Reducing harsh S sounds on lead vocals",
    "Smoothing bright backing vocals so they stack better",
    "Making spoken dialogue easier to listen to for long periods",
    "Taming sharp consonants in podcasts and voiceovers",
    "Controlling occasional harshness on acoustic guitar or cymbals in specific cases",
    "Helping a bright vocal sit in a dense modern mix without sounding piercing",
  ];

  const bestPractices = [
    "Find the exact harsh area before applying reduction. Guessing often leads to dull results.",
    "Use only as much reduction as needed. Too much de-essing can make speech sound lispy or lifeless.",
    "Listen in context with the full mix, because sibilance that sounds obvious in solo may be just right in context.",
    "Try clip gain or automation for problem words if a global de-esser is not solving it cleanly.",
    "Consider whether the real issue is the microphone, vocal technique, or an overly bright EQ boost earlier in the chain.",
  ];

  const relatedLinks = [
    {
      title: "Equalization",
      to: "/learning/equalization",
      description:
        "Learn how EQ shapes tone broadly, while a de-esser only reduces harsh frequencies when they become excessive.",
    },
    {
      title: "Compression",
      to: "/learning/compression",
      description:
        "See how compression can sometimes exaggerate sibilance by bringing quieter consonants forward.",
    },
    {
      title: "Limiting",
      to: "/learning/limiting",
      description:
        "Understand why bright peaks and sibilance can become even more noticeable when a vocal or mix is pushed louder.",
    },
    {
      title: "Saturation",
      to: "/learning/saturation",
      description:
        "Explore how saturation can add presence and harmonics, but sometimes also makes bright consonants more obvious.",
    },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="De-Esser | Parish Audio Learning"
        description={pageDescription}
        canonical={pageUrl}
        schema={schema}
      />

      <Box
        ref={topContainer}
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 260, md: 310 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.84) 0%, rgba(8,12,18,0.74) 38%, rgba(8,12,18,0.42) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.2rem", md: "3.35rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      De-Esser in Music Production
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      A de-esser is a specialized processor designed to reduce harsh
                      sibilance. It is most often used on vocals and spoken word, where
                      sharp S, SH, and CH sounds can jump out and make the recording
                      feel piercing, brittle, or tiring to listen to.
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      The key idea behind a de-esser is selective control. Instead of
                      dulling the whole track with static EQ, it only reduces the harsh
                      area when it becomes excessive. That makes it one of the most
                      useful tools for keeping a vocal bright and clear without letting
                      sibilance become distracting.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: style.color3,
                            lineHeight: 1.55,
                          }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <HearingRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      What Is a De-Esser?
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    A de-esser is a dynamic processor that detects excessive
                    high-frequency sibilance and reduces it only when needed. It is
                    commonly described as a frequency-conscious compressor, because it
                    reacts mainly to a problem region rather than the entire signal
                    equally.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    This makes it ideal for vocals and speech. A singer or speaker may
                    sound clear and balanced most of the time, but certain consonants
                    can suddenly spike in the upper range. Instead of turning the whole
                    track dull, a de-esser controls just those moments.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    De-essing is often subtle when done well. The best settings usually
                    make the voice feel smoother and more polished without drawing
                    attention to the fact that processing is happening.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      How De-Essing Works
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {deEsserConcepts.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Typography sx={{ color: accent, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    In practice, the de-esser listens for a chosen high-frequency area.
                    When a harsh consonant becomes too strong, the processor reduces
                    that part of the sound briefly. Once the sibilance passes, the
                    signal returns to normal.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GraphicEqRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Common Types of De-Essers
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {deEsserTypes.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.65 }}
                        >
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      De-Esser vs EQ
                    </Typography>
                  </Stack>

                  <Paper
                    elevation={0}
                    sx={{
                      ...subtlePanelSx,
                      p: 1.5,
                    }}
                  >
                    <Stack spacing={1.2}>
                      <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                        Static control vs dynamic control
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        EQ turns a frequency range down all the time. A de-esser only
                        reduces it when it becomes a problem. That is why de-essing
                        often sounds more natural on vocals that need to stay bright
                        but not harsh.
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        If a vocal is overly bright all the time, EQ may be the right
                        solution. If the harshness only appears on certain words or
                        consonants, a de-esser is often the better choice.
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TuneRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Common De-Esser Controls
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {controls.map((item) => (
                      <Paper
                        key={item.name}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Box>
                            <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                              {item.name}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: style.color3, lineHeight: 1.65 }}
                            >
                              {item.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LibraryMusicRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        How De-Essers Are Used
                      </Typography>
                    </Stack>

                    <Stack spacing={1.1}>
                      {commonUses.map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <LibraryMusicRoundedIcon
                            sx={{ color: accent, mt: "2px", fontSize: 20, flexShrink: 0 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                      De-essing is often most effective after compression, because
                      compression can make sibilance more obvious by bringing quieter
                      details forward. It is also commonly used after bright EQ boosts
                      that add clarity but also expose harsh consonants.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WarningAmberRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        When De-Essing Goes Too Far
                      </Typography>
                    </Stack>

                    <Stack spacing={1.1}>
                      {[
                        "The voice can start sounding lispy, smeared, or oddly soft on consonants.",
                        "Important clarity and intelligibility can be reduced along with the harshness.",
                        "The vocal may lose air and life if the chosen frequency area is too broad.",
                        "Aggressive settings can make the processing obvious instead of invisible.",
                      ].map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <WarningAmberRoundedIcon
                            sx={{ color: accent, mt: "2px", flexShrink: 0, fontSize: 20 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Best Practices for Using a De-Esser
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {bestPractices.map((item, index) => (
                      <Paper
                        key={index}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                        }}
                      >
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TipsAndUpdatesRoundedIcon
                            sx={{ color: accent, mt: "2px", flexShrink: 0 }}
                          />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography
                    sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                  >
                    Related Learning Topics
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {relatedLinks.map((item) => (
                      <Paper
                        key={item.title}
                        elevation={0}
                        sx={{
                          ...subtlePanelSx,
                          p: 1.5,
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          minHeight: "75%",
                        }}
                      >
                        <Box>
                          <Typography
                            sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}
                          >
                            {item.title}
                          </Typography>
                          <Typography
                            sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}
                          >
                            {item.description}
                          </Typography>
                        </Box>

                        <Box>
                          <Button
                            component={RouterLink}
                            to={item.to}
                            sx={{
                              borderRadius: "5px",
                              textTransform: "none",
                              fontWeight: 700,
                              color: style.color4,
                              backgroundColor: "rgba(255,255,255,0.05)",
                              border: `1px solid ${accent}`,
                              "&:hover": {
                                borderColor: style.color4,
                                backgroundColor: "rgba(255,255,255,0.10)",
                              },
                            }}
                          >
                            Read More
                          </Button>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <GraphicEqRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Final Thoughts
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    A de-esser is a small tool that can make a huge difference in how
                    polished a vocal or voice recording feels. By controlling harsh
                    sibilance only when it becomes excessive, it helps keep a track
                    smooth, clear, and comfortable to hear.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    The best de-essing usually goes unnoticed. It keeps the brightness
                    and clarity you want while gently removing the moments that would
                    otherwise distract the listener.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.7 }}>
                  * Parish Audio is not affiliated with any brands, products, or artists
                  mentioned for educational context on this page. *
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}